// Foto do aluno — tirar pela câmera do dispositivo (getUserMedia) ou escolher
// um arquivo já existente, com preview antes de confirmar. Sempre redimensiona
// pro lado maior caber em MAX_LADO antes de enviar (webcam/celular tiram foto
// em resolução bem maior que o necessário pra um avatar — isso mantém o
// arquivo pequeno tanto pro upload quanto pro custo de armazenamento no R2,
// ver backend/storage.js). Upload é imediato (POST /api/alunos/:id/foto), não
// espera o form principal ser salvo — por isso só aparece habilitado em modo
// de edição (aluno já tem um id de verdade).
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, Upload, X, Check, Trash2, RotateCcw, Loader2, User } from 'lucide-react';
import { Button } from './ui';
import { useToast, useConfirm } from './Feedback';
import { API_URL } from '../api/config';
import { useApi } from '../api/useApi';
import { enquadrarFotoDocumento } from '../utils/faceCrop';

const MAX_LADO = 480;

// Desenha uma fonte de imagem (vídeo ou <img>) num canvas redimensionado,
// devolve um data URL JPEG comprimido.
function paraDataUrlComprimido(fonte, largura, altura) {
  const escala = Math.min(1, MAX_LADO / Math.max(largura, altura));
  const w = Math.round(largura * escala);
  const h = Math.round(altura * escala);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(fonte, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', 0.82);
}

function FotoAluno({ alunoId, fotoUrl, authHeaders, onFotoAtualizada, disabled }) {
  const api = useApi();
  const toast = useToast();
  const confirm = useConfirm();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const [cameraAberta, setCameraAberta] = useState(false);
  const [preview, setPreview] = useState(null); // data URL, antes de confirmar
  const [enviando, setEnviando] = useState(false);
  const [processandoEnquadramento, setProcessandoEnquadramento] = useState(false);

  const pararCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    setCameraAberta(false);
  }, []);

  // Só anexa o stream no <video> depois que ele já existe no DOM (o elemento
  // só é montado quando cameraAberta vira true, ver JSX abaixo) — um
  // useEffect, ao contrário de setTimeout(0), garante isso mesmo se o
  // navegador demorar mais um tick pra commitar o DOM. `.play()` explícito
  // porque `autoPlay` sozinho às vezes não dispara quando o srcObject é
  // atribuído via JS depois do elemento já montado.
  useEffect(() => {
    if (cameraAberta && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [cameraAberta]);

  // Some navegadores/páginas nunca chegam a existir aqui: getUserMedia só
  // existe em contexto seguro (https ou localhost) — testar no celular pelo
  // IP da rede (http://192.168...) por exemplo cai direto nesse erro, sem
  // nem mostrar o pedido de permissão.
  const abrirCamera = async () => {
    setPreview(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error(
        'Câmera não disponível neste navegador/endereço — funciona em https ou localhost. Use "Escolher Arquivo".',
      );
      return;
    }
    try {
      // 'environment' = câmera TRASEIRA (a foto é do inscrito, não de quem
      // está segurando o celular) — Android e iOS entendem esse valor da
      // mesma forma; sem isso caía na frontal por padrão em qualquer
      // aparelho com mais de uma câmera.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      streamRef.current = stream;
      setCameraAberta(true);
    } catch (err) {
      const mensagens = {
        NotAllowedError:
          'Permissão da câmera negada — libere o acesso nas configurações do navegador e tente de novo.',
        NotFoundError: 'Nenhuma câmera encontrada nesse dispositivo. Use "Escolher Arquivo".',
        NotReadableError: 'A câmera está sendo usada por outro programa/aba.',
      };
      toast.error(
        mensagens[err.name] ||
          'Não foi possível acessar a câmera. Verifique a permissão do navegador ou escolha um arquivo.',
      );
    }
  };

  const tirarFoto = async () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      toast.error(
        'A câmera ainda não carregou a imagem — espere um instante e tente capturar de novo.',
      );
      return;
    }

    const largura = video.videoWidth;
    const altura = video.videoHeight;

    // Congela o frame em um canvas antes de parar a câmera
    const canvasTemp = document.createElement('canvas');
    canvasTemp.width = largura;
    canvasTemp.height = altura;
    canvasTemp.getContext('2d').drawImage(video, 0, 0, largura, altura);
    pararCamera();

    setProcessandoEnquadramento(true);
    try {
      const { dataUrl, rostoDetectado } = await enquadrarFotoDocumento(canvasTemp, largura, altura, {
        maxLado: MAX_LADO,
      });
      setPreview(dataUrl);
      if (rostoDetectado) {
        toast.success('Rosto detectado e enquadrado automaticamente (padrão documento).');
      }
    } catch {
      setPreview(paraDataUrlComprimido(canvasTemp, largura, altura));
    } finally {
      setProcessandoEnquadramento(false);
    }
  };

  const escolherArquivo = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Escolha um arquivo de imagem.');
      return;
    }
    const img = new Image();
    const leitor = new FileReader();
    setProcessandoEnquadramento(true);
    leitor.onload = () => {
      img.onload = async () => {
        try {
          const { dataUrl, rostoDetectado } = await enquadrarFotoDocumento(
            img,
            img.naturalWidth,
            img.naturalHeight,
            { maxLado: MAX_LADO },
          );
          setPreview(dataUrl);
          if (rostoDetectado) {
            toast.success('Rosto detectado e enquadrado automaticamente (padrão documento).');
          }
        } catch {
          setPreview(paraDataUrlComprimido(img, img.naturalWidth, img.naturalHeight));
        } finally {
          setProcessandoEnquadramento(false);
        }
      };
      img.src = leitor.result;
    };
    leitor.readAsDataURL(file);
  };

  const confirmarFoto = async () => {
    if (!preview) return;
    setEnviando(true);
    try {
      const res = await fetch(`${API_URL}/api/alunos/${alunoId}/foto`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ imagem_base64: preview }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Erro ao enviar foto.');
      toast.success('Foto atualizada.');
      setPreview(null);
      const fotoUrlAtualizada = data?.data?.foto_url ?? data?.foto_url;
      onFotoAtualizada(fotoUrlAtualizada);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setEnviando(false);
    }
  };

  const removerFoto = async () => {
    if (
      !(await confirm('Remover a foto desse inscrito?', {
        title: 'Remover foto',
        confirmText: 'Remover',
      }))
    )
      return;
    try {
      await api.del(`/api/alunos/${alunoId}/foto`);
      onFotoAtualizada(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const avatarAtual = preview || fotoUrl;

  return (
    <div className="flex items-center gap-4">
      <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center flex-shrink-0 relative">
        {processandoEnquadramento ? (
          <div className="flex flex-col items-center justify-center p-2 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            <span className="text-[9px] text-gray-500 mt-1 font-medium">Enquadrando...</span>
          </div>
        ) : avatarAtual ? (
          <img src={avatarAtual} alt="" className="w-full h-full object-cover" />
        ) : (
          <User className="w-8 h-8 text-gray-300 dark:text-gray-600" />
        )}
      </div>

      {disabled ? (
        <p className="text-xs text-gray-400 dark:text-gray-500">
          Salve o cadastro primeiro pra poder adicionar uma foto.
        </p>
      ) : cameraAberta ? (
        // Câmera em tela cheia (position: fixed cobre o viewport inteiro,
        // por cima do menu/header) — igual a qualquer app de câmera nativo,
        // em vez do quadradinho de 160px que ficava espremido do lado do
        // avatar. `z-[100]` fica acima do Modal (z-50) pra funcionar mesmo
        // se um dia essa tela abrir dentro de um modal.
        <div className="fixed inset-0 z-[100] bg-black flex flex-col">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="flex-1 w-full h-full object-cover"
          />

          {/* Guia visual de enquadramento (estilo documento) */}
          <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center">
            <div className="w-56 h-72 border-2 border-white/30 border-dashed rounded-[50%] mb-12 flex items-center justify-center">
              <span className="text-[11px] text-white/60 tracking-wider font-medium uppercase bg-black/40 px-2.5 py-1 rounded-full">
                Posicione o rosto
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={pararCamera}
            aria-label="Cancelar"
            className="absolute right-4 w-11 h-11 rounded-full bg-black/50 flex items-center justify-center text-white"
            style={{ top: 'max(1rem, env(safe-area-inset-top))' }}
          >
            <X className="w-5 h-5" />
          </button>
          <div
            className="absolute left-0 right-0 flex items-center justify-center"
            style={{ bottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
          >
            <button
              type="button"
              onClick={tirarFoto}
              aria-label="Capturar foto"
              className="w-[72px] h-[72px] rounded-full bg-white ring-4 ring-white/40 active:scale-95 transition-transform"
            />
          </div>
        </div>
      ) : processandoEnquadramento ? (
        <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 font-medium">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Ajustando enquadramento no rosto e ombros...</span>
        </div>
      ) : preview ? (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="primary"
            icon={Check}
            disabled={enviando}
            onClick={confirmarFoto}
          >
            {enviando ? 'Enviando...' : 'Usar essa foto'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            icon={RotateCcw}
            disabled={enviando}
            onClick={() => setPreview(null)}
          >
            Tirar outra
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <Button type="button" size="sm" variant="secondary" icon={Camera} onClick={abrirCamera}>
            Tirar Foto
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            icon={Upload}
            onClick={() => fileInputRef.current?.click()}
          >
            Escolher Arquivo
          </Button>
          {fotoUrl && (
            <button
              type="button"
              onClick={removerFoto}
              title="Remover foto"
              className="w-8 h-8 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={escolherArquivo}
          />
        </div>
      )}
      {enviando && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
    </div>
  );
}

export default FotoAluno;
